import {isArray, isObject, isUndefined, keys, last, mapValues, max, merge, size} from 'lodash';
import {Store} from 'redux';

import {globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment} from '../../libs/shared/helpers/src/environment/envHelpers';
import {isMobile} from '../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {leaveBreadcrumb, reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../srcCommon/services/logger/logger';
import {IdleIterationSummary} from '../helpers/async/inIdleCallback';
import {withTimeoutAsync} from '../helpers/asyncHelpers';
import {applyGc} from '../store/appActions';
import {AppState} from '../store/appState';
import {AppStore} from '../store/appStore';
import {getOriginalData} from '../store/data/dataSelectors';
import {
  getHasGcRemoveCycles,
  getHasPerformanceDebugLogging,
} from '../store/featureFlags/featureFlagsSelectors';
import {CellsPanelPageTypesEnum} from '../store/panels/cells/cellsPanelState';
import {getCellsPanels} from '../store/panels/panelsSelectors';
import {cleanStateAsync} from './gc/tricolorGc';

declare global {
  interface Window {
    gcFront: (stateChangesToView?: string[], diffType?: DiffType) => void | Object;
  }
}

/*
 * Constants.
 */

const localGCIntervalTime = 2.5 * 60 * 1000; // 2.5 minutes.
const prodGCIntervalTime = 10 * 60 * 1000; // 10 minutes.

// If we are running locally, we should increase the GC time frame. This will help
// show GC errors that may happen.
const gcIntervalTime = isDevelopment() ? localGCIntervalTime : prodGCIntervalTime;

/*
 * Types.
 */

type DiffType = 'total' | 'value';

/*
 * Service.
 */

export function setupGc(store: Store<AppState>) {
  let isGcRunning = false;

  const tryRunGcOnceAsync = async (stateChangesToView?: string[], diffType?: DiffType) => {
    // Make sure we have only one GC cycle in progress at once.
    if (isGcRunning) {
      throw new Error('Garbage collector already running');
    }

    isGcRunning = true;
    try {
      return await gc(store, stateChangesToView, diffType);
    } catch (error) {
      reportToBugsnag(error);
      return undefined;
    } finally {
      isGcRunning = false;
    }
  };

  const runGcLoopAsync = async () => {
    const startTime = performance.now();
    try {
      // Start a GC cycle.
      const gcPromise = tryRunGcOnceAsync();

      // Log when the cycle takes longer than the interval so we know how often this happens.
      withTimeoutAsync(
        () => gcPromise,
        gcIntervalTime,
        () => {
          const state = store.getState();
          const resourceMapSizes = measureResourceMaps(state);
          const cellsPanelSizes = measureCellsPanels(state);

          logger.getLogger('gc').warn('Garbage collection running long', {
            resource_map_sizes: resourceMapSizes,
            cells_panel_sizes: cellsPanelSizes,
          });
        },
      );

      // Wait for the GC cycle to finish before starting the next one.
      await gcPromise;
    } finally {
      // Schedule the next GC cycle. If the previous cycle took longer than the interval, schedule it immediately.
      const duration = performance.now() - startTime;
      setTimeout(runGcLoopAsync, Math.max(1, gcIntervalTime - duration));
    }
  };

  // Start GC'ing periodically.
  runGcLoopAsync();

  /**
   * gcFront can be called directly from the console, both in development and production. This function also
   * has functionality to create diff's from paramaters passed in.
   *
   * @param stateChangesToView [Optional] Specifies the fields you would like returned in the diff.
   *                           if this is not specified, no diff will be returned. You can specify
   *                           a wildcard to split an object into individual diffs.
   *                           E.g. ['data.cards', 'data.calendarEvents.*.participants']
   * @param diffType [Optional][Default: total] Controls how the diff is displayed.
   *                    total: Returns the total count of the results
   *                    value: Returns the value of the resource
   * @returns void or diff object if stateChangesToView is specified.
   */
  globalWindow.gcFront = async (stateChangesToView?: string[], diffType?: DiffType) => {
    const startTime = performance.now();
    const stateChange = await tryRunGcOnceAsync(stateChangesToView, diffType);
    const endTime = performance.now();

    if (!isMobile()) {
      console.log(
        'Completed Garbage Collection Request:',
        `\n\tTime Elapsed: ${Math.round(endTime - startTime)}ms`,
      );
    }

    return stateChange;
  };
}

export async function gc(
  store: AppStore,
  stateChangesToView?: string[],
  diffType: DiffType = 'total',
): Promise<void | Object> {
  const state = store.getState();
  const hasLogging = getHasPerformanceDebugLogging(state);

  const stateChange = generateStateChangeView(diffType, state, undefined, stateChangesToView);

  const cleanStartTime = performance.now();
  const {newState, iterationSummary} = await performClean(store);
  const cleanDuration = performance.now() - cleanStartTime;

  // Leave a breadcrumb in case this GC cycle results in a crash.
  leaveBreadcrumb('[gc] Garbage collection completed.', {});

  const applyStartTime = performance.now();
  store.dispatch(applyGc(newState));
  const applyDuration = performance.now() - applyStartTime;

  if (hasLogging) {
    const measureStartTime = performance.now();
    const resourceMapSizes = measureResourceMaps(state);
    const cellsPanelSizes = measureCellsPanels(state);
    const measureDuration = performance.now() - measureStartTime;

    logger.getLogger('gc').info('Garbage collection completed', {
      has_contact_resources_gc: true,
      has_tricolor_gc: true,
      has_gc_remove_cycles: getHasGcRemoveCycles(state),
      gc_clean_duration: cleanDuration,
      gc_apply_duration: applyDuration,
      // Include only the true garbage collection work in the total duration (not the logging measurements).
      duration: cleanDuration + applyDuration,
      gc_measure_duration: measureDuration,
      resource_map_sizes: resourceMapSizes,
      cells_panel_sizes: cellsPanelSizes,
      max_blocking_duration: max(iterationSummary?.blockingDurations || [0]),
      full_max_blocking_duration: computeFullMaxBlockingDuration(
        cleanDuration,
        applyDuration,
        iterationSummary?.blockingDurations || [],
      ),
      idle_cycle_count: iterationSummary?.idleCycleCount,
    });
  }

  if (stateChangesToView) {
    return generateStateChangeView(
      diffType,
      {...newState, data: newState.originalData},
      stateChange,
      stateChangesToView,
    );
  }

  return undefined;
}

/*
 * Helpers.
 */

function computeFullMaxBlockingDuration(
  cleanDuration: number,
  applyDuration: number,
  idleCycleBlockingDurations: ReadonlyArray<number>,
) {
  const lastIdleCycleBlockingDuration = last(idleCycleBlockingDurations);
  if (lastIdleCycleBlockingDuration === undefined) {
    // No idle cycles were taken (the GC ran when the window was hidden), so we weren't blocked.
    return 0;
  }

  // The apply step is ran in the last idle cycle but not captured in the blocking durations.
  const lastIdleCycleBlockDuration = applyDuration + lastIdleCycleBlockingDuration;
  return max([...idleCycleBlockingDurations, lastIdleCycleBlockDuration]);
}

async function performClean(store: AppStore) {
  const originalState = store.getState();

  const shouldKeepCycles = !getHasGcRemoveCycles(originalState);
  let iterationSummary: IdleIterationSummary | undefined;
  const onIterationSummary = (summary: IdleIterationSummary) => {
    iterationSummary = summary;
  };

  const newState = await cleanStateAsync(store.getState, shouldKeepCycles, onIterationSummary);

  return {
    newState,
    iterationSummary,
  };
}

function generateStateChangeView(
  type: DiffType,
  state: AppState,
  currentDiff: any,
  stateChangesToView?: string[],
) {
  if (!stateChangesToView || stateChangesToView.length === 0) {
    return {};
  }

  return stateChangesToView.reduce((diff, change) => {
    const split = change.split('.');
    return merge(diff, generateStateDiff(type, state, diff[change] ?? {}, split, isUndefined(currentDiff)));
  }, currentDiff ?? {});
}

function generateStateDiff(
  type: DiffType,
  stateStep: any,
  currentObj: any,
  currentKeys: string[],
  isFirstPass: boolean,
): any {
  if (currentKeys.length === 0) {
    return currentObj;
  }
  // If we are at the last index of the check, return
  if (currentKeys.length === 1) {
    let value;
    if (currentKeys[0] === '*') {
      throw new Error('Can not have a wildcard (*) at the end.');
    }
    if (type === 'total') {
      if (isArray(stateStep[currentKeys[0]])) {
        value = stateStep[currentKeys[0]].length;
      } else if (isObject(stateStep[currentKeys[0]])) {
        value = keys(stateStep[currentKeys[0]]).length;
      }
    } else if (type === 'value') {
      value = JSON.parse(JSON.stringify(stateStep[currentKeys[0]]));
    }
    // eslint-disable-next-line no-param-reassign
    currentObj[currentKeys[0]] = merge(
      currentObj[currentKeys[0]] ?? {},
      isFirstPass ? {before: value} : {after: value},
    );
    return currentObj;
  }

  // Check if we are looking at a wildcard route
  if (currentKeys[0] === '*') {
    keys(stateStep).forEach((k: string) => {
      // eslint-disable-next-line no-param-reassign
      currentObj[k] = generateStateDiff(
        type,
        stateStep[k] ?? {},
        currentObj[k] ?? {},
        [...currentKeys].splice(1),
        isFirstPass,
      );
    });
    return currentObj;
  }

  // eslint-disable-next-line no-param-reassign
  currentObj[currentKeys[0]] = generateStateDiff(
    type,
    stateStep[currentKeys[0]] ?? {},
    currentObj[currentKeys[0]] ?? {},
    [...currentKeys].splice(1),
    isFirstPass,
  );
  return currentObj;
}

/** Measures the size of the collections in the data state. */
function measureResourceMaps(state: AppState): {[key: string]: number} {
  const data = getOriginalData(state);

  // https://jsben.ch/ysqG6
  return mapValues(data, (value) => (isObject(value) ? size(value) : 0));
}

/** Measures the number of resources referenced and retained by cells panels. */
function measureCellsPanels(state: AppState): ReadonlyArray<{visible: number; hidden: number}> {
  const cellsPanels = getCellsPanels(state);

  return cellsPanels.map((panel) => {
    const page = panel.pages.current ?? panel.pages.next;

    if (!page) {
      return {visible: 0, hidden: 0};
    }

    if (page.type === CellsPanelPageTypesEnum.MESSAGES) {
      return {
        visible: page.messageUids.length,
        hidden: 0,
      };
    }

    return {
      visible: page.conversationIds.length,
      hidden: page.removedConversationIds.length,
    };
  });
}
